import { PrinterOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useEffect, forwardRef, useImperativeHandle, Ref } from 'react';
import { Badge, Button, Table, TableProps, Popover, message, Tooltip } from 'antd';
import StatusButton from 'components/StatusButton';
import { useFlagConfigValue } from 'hooks/business/system/useFlagConfig';
import dayjs from 'lib/dayjs';
import Copy from 'views/Conversation/Sider/Copy';
import accountNumToId from './accountNumToId';
import useOrder from '../hooks/useOrder';
import { PriceText } from './PriceText';
import { useTransAppProductName } from 'hooks/useTrans';
import { isQuickChat } from 'productLine';
import { match, select, when } from 'ts-pattern';
import { formatNumber } from 'utils/numberFormat';
import { useUnit } from '../hooks/useUnit';
import { T, useTranslate } from '@tolgee/react';

const numberFormat = (number: number) => formatNumber(number, '0,0[.]00');

type TR = Pay.Order;

export interface OrderTableProps extends TableProps<TR> {
  onPay: (record: TR) => Promise<any>;
}

export interface OrderTableRef {
  fetchOrderList: ReturnType<typeof useOrder>['fetchList'];
}

function OrderTable(props: OrderTableProps, ref: Ref<OrderTableRef>) {
  const { onPay, className, style, ...otherProps } = props;

  const { t } = useTranslate();
  const appProductNameT = useTransAppProductName();

  const { billQrcode } = useFlagConfigValue();

  const {
    list: orderList,
    loading: orderListLoading,
    fetchList: fetchOrderList,
    page: orderPage,
  } = useOrder();

  useEffect(() => {
    fetchOrderList({
      pageNo: 1,
      pageSize: 10,
    }).catch(() => {
      message.error(t('market.获取订单信息失败，请稍后再试'));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { formatter } = useUnit();

  useImperativeHandle(ref, () => ({
    fetchOrderList,
  }));

  return (
    <Container className={className} style={style}>
      <Toolbar>
        <div />
        <Popover
          placement="bottomRight"
          content={
            <PopoverContent>
              <div>
                {t('market.请扫描下方二维码')}
                <br />
                {appProductNameT('market.contactOrderInvoiceQRTips')}
              </div>
              <img src={billQrcode} alt="bill" />
            </PopoverContent>
          }
        >
          <Button icon={<PrinterOutlined />}>{t('market.发票说明')}</Button>
        </Popover>
      </Toolbar>
      <Table
        rowKey="orderCode"
        dataSource={orderList}
        loading={orderListLoading}
        columns={[
          {
            key: 'orderCode',
            dataIndex: 'orderCode',
            title: t('market.订单编号'),
            width: 240,
            render: (code: TR['orderCode']) => {
              return (
                <>
                  {code}
                  <Copy
                    text={code}
                    successMessage={t('market.复制成功')}
                    failedMessage={t('market.复制失败')}
                  />
                </>
              );
            },
          },
          {
            key: 'orderAction',
            dataIndex: 'orderAction',
            width: 100,
            title: t('market.订单类型'),
            render: (orderType: TR['orderAction'], record) => {
              if (record.packageId === 11) {
                return t('enterprise.套餐');
              }
              if (record.packageId === 8) {
                return t('market_package_traffic');
              }
              return t({
                key: `订单操作.${orderType}`,
                defaultValue: '',
              });
            },
          },
          {
            key: 'orderDescribe',
            dataIndex: 'orderDescribe',
            title: t('market.订单内容'),
            width: 300,
            render: (describe: TR['orderDescribe'], row: TR) => {
              return match(row)
                .with(
                  {
                    packageId: 8,
                    trafficPackageType: 'INCREASE_THE_MONTHLY_CAP',
                    handleNum: select('handleNum'),
                    orderDescribe: {
                      validStartStr: select('validStartStr'),
                      validEndStr: select('validEndStr'),
                    },
                  },
                  (i) => {
                    return `${t('aiorder_type1_title')} ${t('aiorder_renci', {
                      x: numberFormat(i.handleNum || 0),
                    })} ${t('market.有效期')}: ${i.validStartStr} - ${i.validEndStr}`;
                  },
                )
                .with(
                  {
                    packageId: 8,
                    trafficPackageType: 'TRAFFIC_PACKAGE',
                    orderDescribe: {
                      durationNum: select('durationNum'),
                    },
                    handleNum: select('handleNum'),
                  },
                  (i) => {
                    return `${t('aiorder_type2_title')} ${t('aiorder_renci', {
                      x: numberFormat(i.handleNum || 0),
                    })} ${t('aiorder_limit_x_month', { x: i.durationNum })}`;
                  },
                )
                .with(
                  {
                    packageId: 8,
                    trafficPackageType: 'ENTERPRISE_TRAFFIC_PACKAGE',
                    handleNum: select('handleNum'),
                  },
                  (i) => {
                    return `${t('aiorder_type3_title')} ${t('aiorder_renci', {
                      x: formatter(i.handleNum || 0),
                    })} ${t('aiorder_limit_1_year')}`;
                  },
                )
                .with(
                  {
                    packageId: when((id) => id === 11),
                    orderNumber: select('orderNumber'),
                  },
                  (i) => {
                    return `${t('AI精准回复能力护航的订单数')} ${t('{x}单', {
                      x: numberFormat(i.orderNumber),
                    })}`;
                  },
                )
                .otherwise(() => {
                  const { promotionType } = row;
                  const { packageName, accountNum, nonReceptionAccountNum, durationNum } = describe;
                  const { orderAction, packageId } = row;
                  const name = isQuickChat
                    ? appProductNameT('quickchat_workbench')
                    : t({
                        key: `packageName.${packageName}`,
                        defaultValue: packageName,
                      });

                  const packId = packageId || accountNumToId[accountNum];

                  const promotionText = promotionType
                    ? `(${t(`promotionType.${promotionType}` as any)})`
                    : undefined;

                  const accountText =
                    nonReceptionAccountNum > 0 ? (
                      <T
                        keyName="account.不限额帐号x位限额帐号y位"
                        params={{
                          x: accountNum,
                          y: nonReceptionAccountNum,
                          num: <></>,
                        }}
                      />
                    ) : (
                      `${accountNum}${t('market.帐号')}`
                    );

                  if (orderAction === 'UPGRADE') {
                    return (
                      <>
                        {`${name} ${t('market.升级套餐几', {
                          s: t(`套餐.${packId}`) ?? '',
                        })} `}
                        {accountText}
                        {` ${durationNum > 0 ? t('market.增加套餐时长') : ''} ${t(
                          `套餐时长.${durationNum}`,
                        )}`}
                        {promotionText}
                      </>
                    );
                  }
                  if (orderAction === 'RENEW') {
                    return (
                      <>
                        {`${name} ${t('market.续订套餐几', {
                          s: t(`套餐.${packId}`) ?? '',
                        })} `}
                        {accountText}
                        {` ${t(`套餐时长.${durationNum}`)}`}
                        {promotionText}
                      </>
                    );
                  }

                  return (
                    <>
                      {`${name} ${t(`套餐.${packId}`) || ''} `}
                      {accountText}
                      {` ${t(`套餐时长.${durationNum}`)}`}
                      {promotionText}
                    </>
                  );
                });
            },
          },
          {
            key: 'amount',
            dataIndex: 'amount',
            title: t('market.订单金额'),
            render: (amount: TR['amount'], order) => {
              return <PriceText currency={order.currency} price={amount} />;
            },
          },
          {
            key: 'updateTime',
            dataIndex: 'updateTime',
            title: () => {
              return (
                <div className="flex items-center">
                  <div className="mr-[2px]">{t('订购时间')}</div>
                  <Tooltip
                    title={t(
                      '若您的订单未完成支付，将显示订单创建时间。订单完成支付后，更新为订单支付时间。',
                    )}
                  >
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
              );
            },
            render: (time: TR['updateTime'], record) => {
              return record.orderStatus === 2
                ? dayjs(parseInt(time, 10)).format('YYYY-MM-DD HH:mm:ss')
                : dayjs(parseInt(record.createTime, 10)).format('YYYY-MM-DD HH:mm:ss');
            },
          },
          {
            key: 'orderStatus',
            dataIndex: 'orderStatus',
            title: t('market.状态'),
            render: (status: TR['orderStatus']) => {
              return {
                1: <Badge status="error" text={t('market.待支付')} />,
                2: <Badge status="success" text={t('market.已完成')} />,
                3: <Badge status="default" text={t('market.已取消')} />,
                4: <Badge status="default" text={t('market.已过期')} />,
              }[status];
            },
          },
          {
            key: 'operation',
            title: t('market.操作'),
            render: (_, record: TR) => {
              const { orderStatus } = record;

              if (orderStatus !== 1) {
                return '-';
              }

              return (
                <Operation type="link" onClick={() => onPay(record)}>
                  {t('translation.支付')}
                </Operation>
              );
            },
          },
        ]}
        pagination={{
          ...orderPage,
          onChange: (page, pageSize) => {
            fetchOrderList({
              pageNo: page,
              pageSize,
            }).catch(() => {
              message.error(t('market.获取订单信息失败，请稍后再试'));
            });
          },
        }}
        {...otherProps}
      />
    </Container>
  );
}

export default forwardRef(OrderTable);

const Container = styled.div``;

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  margin-bottom: 16px;
  > h3 {
    position: relative;
    margin-bottom: 0;
    text-indent: 10px;
    font-size: 16px;
    font-weight: 500;
    color: var(--fett-text-primary);
    &::before {
      display: inline-block;
      content: '';
      position: absolute;
      top: 5px;
      left: 0;
      width: 4px;
      height: 14px;
      background: #3370ff;
    }
  }
`;

const PopoverContent = styled.div`
  img {
    width: 100px;
    height: 100px;
  }
`;

const Operation = styled(StatusButton)`
  padding-left: 0;
`;
